import React, { useCallback, useEffect, useState } from 'react';
import type { CustomFieldValues } from 'components/common/customFields/types';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle, Box, Button, TextField, Typography } from '@mui/material';
import Loader from 'components/common/Loader';
import { locationTypeOptions, jobTypeOptions } from './constants';
import CloseIcon from '@mui/icons-material/Close';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import Editor from 'components/common/editor';
import ClientsAutoComplete from 'components/common/ClientsAutocomplete';
import { renderField, validateUrl } from 'components/utils';
import CustomFieldsForm from 'components/common/CustomFieldsForm';
import Uploader from 'components/common/Uploader';
import MassImportLink from 'components/common/MassImportLink';
import {
  FormTextField,
  FormSelect,
  FormRadioGroup,
  FormCustom,
  scrollToFirstFormErrorOnInvalid,
} from 'components/common/form';
import { useForm } from 'react-hook-form';
import Modal from 'components/common/Modal';
import StatusSelector from 'components/common/StatusSelector';
import { User } from 'components/types';
import { Job, JobFormData } from './types';
import Group from 'components/common/Group';
import JobsAutoComplete from './JobSearch';
import { useAutoSave, useEntitySimpleFieldAutosave, AutoSaveStatusIndicator, normalizeAttachmentsForAutosave } from '../../hooks/autoSave';
import StackTabs from 'components/common/StackTabs';
import { useSession } from 'components/SessionContext';
import RecruiterLiteFieldsForm from './autofill/RecruiterLiteFieldsForm';
import ZipRecruiterFieldsForm from './autofill/ZipRecruiterFieldsForm';
import IndeedFieldsForm from './autofill/IndeedFieldsForm';
import { LinkedInAutofillInfoAlert, ZipRecruiterAutofillInfoAlert, IndeedAutofillInfoAlert } from './autofill/jobAutofillAlerts';
import { buildRecruiterLiteFieldsFromJob, emptyRecruiterLiteFields } from './autofill/recruiterLiteConstants';
import { buildZipRecruiterFieldsFromJob, emptyZipRecruiterFields } from './autofill/zipRecruiterConstants';
import { buildIndeedFieldsFromJob, emptyIndeedFields } from './autofill/indeedConstants';
import { useExtensionDetection } from 'lib/useExtensionDetection';

const schema = yup
  .object({
    title: yup.string().required('Title is required'),
    location: yup.string().required('Location is required'),
    locationType: yup.string(),
    jobType: yup.string(),
    jobTypeCustom: yup.string(),
    recruiterId: yup.string().required('Recruiter is required'),
    clientId: yup.string().required('Client is required'),
    salary: yup.string(),
    attachments: yup.array().of(
      yup.object({
        name: yup.string(),
        url: yup.string(),
        date: yup.date(),
      }),
    ),
    notes: yup.string(),
    statusId: yup.string().required('Status is required'),
    description: yup.string(),
    customFieldsValues: yup.object(),

    autosourceCurrentCompanyFilter: yup.string(),
    autosourcePastCompanyFilter: yup.string(),
    autosourceFunctionFilter: yup.string(),
    autosourceCurrentJobTitleFilter: yup.string(),
    autosourceYearsInCurrrentCompanyFilter: yup.string(),
    autosourceLocationFilter: yup.string(),
    autosourceKeywordSearch: yup.string(),
  })
  .required();

const getAutosaveFormSnapshot = (
  values: JobFormData,
  customFields: CustomFieldValues,
  recruiterLite: Job['recruiterLiteFields'],
  zipRecruiter: Job['zipRecruiterFields'],
  indeed: Job['indeedFields'],
) => ({
  ...values,
  attachments: normalizeAttachmentsForAutosave(values.attachments),
  customFieldsValues: customFields,
  recruiterLiteFields: recruiterLite,
  zipRecruiterFields: zipRecruiter,
  indeedFields: indeed,
});

const getAutosaveValidationFields = (changedKeys: string[], jobType: string | undefined) => {
  const fields: string[] = [];

  for (const key of changedKeys) {
    if (key === 'title' || key === 'location' || key === 'recruiterId' || key === 'clientId' || key === 'statusId') {
      fields.push(key);
    } else if (key === 'jobTypeCustom' && jobType === 'Custom') {
      fields.push('jobTypeCustom');
    }
  }

  return fields;
};

type Props = {
  onClose: () => void;
  onSuccess: (response: Job) => void;
  open: boolean;
  object?: Job;
  defaultTab?: number;
  hideCloseButton?: boolean;
  extraFooterButtons?: React.ReactNode;
};

export const boxStyle = {
  backgroundColor: 'background.paper',
  p: 2,
  borderRadius: 2,
  mb: 2,
  boxShadow: 1,
};

const JobForm = ({ open, onClose, onSuccess, object, defaultTab, extraFooterButtons, hideCloseButton }: Props) => {
  const { user } = useSession();
  const jobO = object || ({} as Job);
  const isUpdate = Boolean(jobO._id);

  const [jobUrl, setJobUrl] = useState('');
  const [tabValue, setTabValue] = useState(defaultTab || 0);
  const [extracting, setExtracting] = useState(false);
  const [isExtracted, setIsExtracted] = useState(false);
  const [existingJobId, setExistingJobId] = useState('');
  const [createFromUrlError, setCreateFromUrlError] = useState('');

  const [editorHash, setEditorHash] = useState(Math.random().toString());
  const [attachmentsHash, setAttachmentsHash] = useState(Math.random().toString());
  const [statusHash, setStatusHash] = useState(Math.random().toString());
  const [clientHash, setClientHash] = useState(Math.random().toString());
  const [customFieldsValues, setCustomFieldsValues] = useState(jobO.customFieldsValues || {});
  const [recruiterLiteFields, setRecruiterLiteFields] = useState(buildRecruiterLiteFieldsFromJob(jobO));
  const [zipRecruiterFields, setZipRecruiterFields] = useState(buildZipRecruiterFieldsFromJob(jobO));
  const [indeedFields, setIndeedFields] = useState(buildIndeedFieldsFromJob(jobO));
  const [clientName, setClientName] = useState(jobO.clientName);

  const [saving, setSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [users, setUsers] = useState<User[]>([]);
  const { installed: extensionInstalled } = useExtensionDetection();

  const { control, handleSubmit, setValue, clearErrors, watch, getValues, trigger } = useForm<
    JobFormData,
    unknown,
    yup.InferType<typeof schema>
  >({
    resolver: yupResolver(schema),
    defaultValues: {
      title: jobO.title,
      location: jobO.location,
      locationType: jobO.locationType,
      jobType: jobO.jobType,
      jobTypeCustom: jobO.jobTypeCustom,
      recruiterId: jobO.recruiterId || user?._id || '',
      clientId: jobO.clientId,
      salary: jobO.salary,
      attachments: jobO.attachments,
      notes: jobO.notes,
      statusId: jobO.statusId,
      description: jobO.description,

      autosourceCurrentCompanyFilter: jobO.autosourceCurrentCompanyFilter,
      autosourcePastCompanyFilter: jobO.autosourcePastCompanyFilter,
      autosourceFunctionFilter: jobO.autosourceFunctionFilter,
      autosourceCurrentJobTitleFilter: jobO.autosourceCurrentJobTitleFilter,
      autosourceYearsInCurrrentCompanyFilter: jobO.autosourceYearsInCurrrentCompanyFilter,
      autosourceLocationFilter: jobO.autosourceLocationFilter,
      autosourceKeywordSearch: jobO.autosourceKeywordSearch,
    },
  });

  // Watch all form values for auto-save
  const watchedValues = watch();

  const autoSave = useAutoSave({
    enabled: !isUpdate,
    type: 'job',
    data: {
      ...watchedValues,
      clientName,
      customFieldsValues,
      recruiterLiteFields,
      zipRecruiterFields,
      indeedFields,
    },
    onGetDraft: (
      draft: JobFormData & {
        clientName?: string;
        recruiterLiteFields?: Job['recruiterLiteFields'];
        zipRecruiterFields?: Job['zipRecruiterFields'];
        indeedFields?: Job['indeedFields'];
      },
    ) => {
      if (!jobO._id && draft) {
        setValue('title', draft.title);
        setValue('location', draft.location);
        setValue('locationType', draft.locationType);
        setValue('jobType', draft.jobType);
        setValue('jobTypeCustom', draft.jobTypeCustom);
        setValue('recruiterId', draft.recruiterId || user?._id || '');
        setValue('salary', draft.salary);
        setValue('attachments', draft.attachments);
        setValue('notes', draft.notes);
        setValue('description', draft.description);
        setValue('statusId', draft.statusId);
        setValue('clientId', draft.clientId);
        setValue('autosourceCurrentCompanyFilter', draft.autosourceCurrentCompanyFilter);
        setValue('autosourcePastCompanyFilter', draft.autosourcePastCompanyFilter);
        setValue('autosourceFunctionFilter', draft.autosourceFunctionFilter);
        setValue('autosourceCurrentJobTitleFilter', draft.autosourceCurrentJobTitleFilter);
        setValue('autosourceYearsInCurrrentCompanyFilter', draft.autosourceYearsInCurrrentCompanyFilter);
        setValue('autosourceLocationFilter', draft.autosourceLocationFilter);
        setValue('autosourceKeywordSearch', draft.autosourceKeywordSearch);
        setClientName(draft.clientName || '');
        setCustomFieldsValues(draft.customFieldsValues || {});
        setRecruiterLiteFields(draft.recruiterLiteFields || emptyRecruiterLiteFields());
        setZipRecruiterFields(draft.zipRecruiterFields || emptyZipRecruiterFields());
        setIndeedFields(draft.indeedFields || emptyIndeedFields());

        setClientHash(Math.random().toString());
        setEditorHash(Math.random().toString());
        setAttachmentsHash(Math.random().toString());
        setStatusHash(Math.random().toString());
      }
    },
  });

  const fetchUsers = async () => {
    try {
      const response = await sendApiRequest<{ list: User[] }>({
        path: '/users',
        method: 'GET',
      });

      setUsers(response.list);
    } catch (e) {
      notify(e);
      setUsers([]);
    } finally {
      if (isUpdate) {
        setDataLoaded(true);
      }
    }
  };

  const extractJobDetails = async (url: string) => {
    if (!validateUrl(url)) {
      setCreateFromUrlError('Invalid URL');
      return;
    }

    setExtracting(true);

    try {
      const response = (await sendApiRequest({
        path: '/jobs/extract-job-details',
        method: 'POST',
        data: { url },
      })) as {
        jobDetails: { job_title: string; location: string; location_type: string; salary: string };
        jobDescription: string;
      };

      if (response && (Object.keys(response.jobDetails || {}).length > 0 || response.jobDescription)) {
        setIsExtracted(true);
      } else {
        notify('No details found');
      }

      if (response.jobDescription) {
        setValue('description', response.jobDescription);
        setEditorHash(Math.random().toString());
      }

      if (response.jobDetails) {
        const { job_title, location, location_type, salary } = response.jobDetails || {};

        if (job_title) {
          setValue('title', job_title);
        }

        if (location) {
          setValue('location', location);
        }

        if (location_type) {
          const locationType = location_type.toLowerCase();

          if (locationType === 'remote') {
            setValue('locationType', 'Remote');
          } else if (locationType === 'hybrid') {
            setValue('locationType', 'Hybrid');
          } else if (locationType === 'onsite' || locationType === 'on-site') {
            setValue('locationType', 'OnSite');
          }
        }

        if (salary) {
          setValue('salary', salary);
        }
      }
    } catch (error) {
      notify(error);
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  useEffect(() => {
    if (!user?._id) {
      return;
    }
    if (!getValues('recruiterId')) {
      setValue('recruiterId', user._id);
    }
  }, [user?._id]);

  const formValues = watch();
  const serializedValues = JSON.stringify(
    getAutosaveFormSnapshot(
      formValues,
      customFieldsValues,
      recruiterLiteFields,
      zipRecruiterFields,
      indeedFields,
    ),
  );

  const seedSnapshot = useCallback(
    () =>
      JSON.stringify(
        getAutosaveFormSnapshot(
          getValues(),
          customFieldsValues,
          recruiterLiteFields,
          zipRecruiterFields,
          indeedFields,
        ),
      ),
    [getValues, customFieldsValues, recruiterLiteFields, zipRecruiterFields, indeedFields],
  );

  const getValidationFields = useCallback(
    (changedKeys: string[], currentData: Record<string, unknown>) =>
      getAutosaveValidationFields(changedKeys, currentData.jobType as string | undefined),
    [],
  );

  const updateAutosave = useEntitySimpleFieldAutosave({
    enabled: isUpdate,
    isReady: dataLoaded,
    snapshot: serializedValues,
    seedSnapshot,
    isBlocked: () => saving,
    getValidationFields,
    trigger,
    updatePath: '/jobs/update-simple-field',
    idField: 'jobId',
    entityId: jobO._id,
  });

  const onSave = async (data: yup.InferType<typeof schema>) => {
    setSaving(true);

    try {
      const response = (await sendApiRequest({
        path: '/jobs/create',
        method: 'POST',
        data: {
          ...data,
          customFieldsValues,
          recruiterLiteFields,
          zipRecruiterFields,
          indeedFields,
        },
      })) as Job;

      onSuccess(response);

      setSaving(false);

      await autoSave.deleteDraft('job');
    } catch (error) {
      setSaving(false);
      notify(error);
    }
  };

  const renderJobUrl = () => {
    if (isExtracted || jobO._id) {
      return null;
    }

    return (
      <Box sx={boxStyle}>
        <MassImportLink type="Jobs" />

        {extracting && <Loader />}

        <Alert severity="warning" sx={{ mt: 2, mb: 4 }}>
          <AlertTitle>Create from URL</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Paste the job URL to automatically population job details. This could take 1-2 minutes.
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            Make sure that the URL is publicly accessible. For example:
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            LinkedIn URL Format:
          </Typography>
          <Alert variant="outlined" severity="error" icon={<CloseIcon fontSize="inherit" />} sx={{ mb: 2 }}>
            https://www.linkedin.com/jobs/search/?currentJobId=4209633495
            <Typography variant="caption" sx={{ display: 'block', color: theme => theme.palette.text.secondary }}>
              Search URLs are not accessible for extraction
            </Typography>
          </Alert>

          <Alert variant="outlined" severity="success">
            https://www.linkedin.com/jobs/view/4209633495
            <Typography variant="caption" sx={{ display: 'block', color: theme => theme.palette.text.secondary }}>
              Direct view URLs work correctly
            </Typography>
          </Alert>
        </Alert>

        <TextField
          size="small"
          label="Job URL"
          placeholder="https://www.linkedin.com/jobs/view/4209633495"
          data-testid="@job-form-url"
          onChange={e => {
            setJobUrl(e.target.value);
            setCreateFromUrlError('');
          }}
          error={!!createFromUrlError}
          helperText={createFromUrlError}
          fullWidth
          autoFocus
          sx={{ backgroundColor: 'background.paper', color: 'text.primary', marginBottom: 2 }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => extractJobDetails(jobUrl)}
          disabled={extracting}
          data-testid="@job-form-create-from-url"
          sx={{ marginLeft: 'auto', display: 'block' }}
        >
          {extracting ? 'Extracting...' : 'Create from URL'}
        </Button>
      </Box>
    );
  };

  const renderForm = () => {
    const statusId = watch('statusId');
    const clientId = watch('clientId');
    const attachments = watch('attachments');
    const jobType = watch('jobType');

    const flexOptions = {
      label: 3,
      value: 9,
    };

    return (
      <Box sx={{ opacity: extracting ? 0.2 : 1 }} data-testid="@job-form">
        <Group>
          {renderField({
            id: 'job_form_title',
            label: 'Title',
            required: true,
            flexOptions,
            renderControl: () => <FormTextField name="title" control={control} fullWidth testId="@job-form-title" />,
          })}

          {renderField({
            id: 'job_form_location',
            label: 'Location',
            required: true,
            flexOptions,
            renderControl: () => (
              <FormTextField name="location" control={control} fullWidth testId="@job-form-location" />
            ),
          })}

          {renderField({
            id: 'job_form_status',
            label: 'Status',
            required: true,
            flexOptions,
            renderControl: () => (
              <FormCustom
                key={statusHash}
                name="statusId"
                control={control}
                renderControl={error => (
                  <Box sx={{ width: '100%' }}>
                    <StatusSelector
                      defaultValue={statusId}
                      testId={statusId ? '@job-form-status-selected' : '@job-form-status'}
                      tourMenuTaskId="creating_job_from_url"
                      onSelect={value => setValue('statusId', value)}
                      endpoint="/jobs/statuses"
                      error={error}
                    />
                  </Box>
                )}
              />
            ),
          })}

          {renderField({
            id: 'job_form_client',
            label: 'Client',
            required: true,
            flexOptions,
            renderControl: () => {
              return (
                <FormCustom
                  key={clientHash}
                  name="clientId"
                  control={control}
                  hideHelperText
                  renderControl={error => (
                    <Box sx={{ width: '100%' }}>
                      <ClientsAutoComplete
                        defaultValue={
                          clientId
                            ? {
                                _id: clientId,
                                name: clientName,
                              }
                            : undefined
                        }
                        onSelect={client => {
                          setValue('clientId', client?._id || '');
                          setClientName(client?.name || '');
                          clearErrors('clientId');
                        }}
                        error={error}
                        testId={clientId ? '@job-form-client-selected' : '@job-form-client'}
                        tourMenuTaskId="creating_job_from_url"
                      />
                    </Box>
                  )}
                />
              );
            },
          })}

          {renderField({
            id: 'job_form_recruiter',
            label: 'Recruiter',
            required: true,
            flexOptions,
            renderControl: () => (
              <FormSelect
                key={users.length > 0 ? 'loaded' : 'loading'}
                fullWidth
                labelId="recruiter-select-label"
                name="recruiterId"
                control={control}
                size="small"
                options={(users.length > 0 ? users : user ? [user] : []).map((u: User) => ({
                  label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
                  value: u._id,
                }))}
              />
            ),
          })}
        </Group>

        <Group>
          {renderField({
            id: 'job_form_location_type',
            label: 'Location type',
            flexOptions,
            renderControl: () => {
              return (
                <FormRadioGroup
                  name="locationType"
                  control={control}
                  testId="@job-form-location-type"
                  options={[...locationTypeOptions]}
                />
              );
            },
          })}

          {renderField({
            id: 'job_form_job_type',
            label: 'Job type',
            flexOptions,
            renderControl: () => {
              return (
                <>
                  <FormRadioGroup
                    name="jobType"
                    control={control}
                    testId="@job-form-job-type"
                    options={[...jobTypeOptions]}
                    onChange={e => {
                      setValue('jobType', e.target.value);

                      if (e.target.value !== 'Custom') {
                        setValue('jobTypeCustom', '');
                      }
                    }}
                  />
                  {jobType === 'Custom' && (
                    <FormTextField name="jobTypeCustom" control={control} fullWidth placeholder="Job custom type..." />
                  )}
                </>
              );
            },
          })}

          {renderField({
            id: 'job_form_salary',
            label: 'Salary',
            flexOptions,
            renderControl: () => <FormTextField name="salary" control={control} fullWidth />,
          })}

          <br />
          <br />

          {renderField({
            id: 'job_form_attachments',
            label: 'Attachments',
            flexOptions,
            renderControl: () => {
              return (
                <Uploader
                  key={attachmentsHash}
                  label="Upload files"
                  defaultFiles={(attachments || []).map(attachment => ({
                    name: attachment.name,
                    url: attachment.url,
                    date: attachment.date,
                  }))}
                  onChange={results => {
                    setValue('attachments', results as { name?: string; url?: string; date?: Date }[]);
                  }}
                />
              );
            },
          })}
        </Group>

        <CustomFieldsForm
          groupId="job_form_custom_fields"
          type="job"
          initialValues={customFieldsValues}
          onChange={values => setCustomFieldsValues(values)}
        />

        <Group title="Description" bg="white" id="job_form_description">
          <Editor
            style={{ height: '300px' }}
            value={watch('description') || ''}
            onChange={value => setValue('description', value)}
            key={editorHash}
          />
        </Group>

        <RecruiterLiteFieldsForm
          value={recruiterLiteFields}
          onChange={setRecruiterLiteFields}
          infoAlert={<LinkedInAutofillInfoAlert extensionInstalled={extensionInstalled} />}
        />

        <ZipRecruiterFieldsForm
          value={zipRecruiterFields}
          onChange={setZipRecruiterFields}
          infoAlert={<ZipRecruiterAutofillInfoAlert extensionInstalled={extensionInstalled} />}
        />

        <IndeedFieldsForm
          value={indeedFields}
          onChange={setIndeedFields}
          infoAlert={<IndeedAutofillInfoAlert extensionInstalled={extensionInstalled} />}
        />
      </Box>
    );
  };

  const renderTabContent = () => {
    if (tabValue === 0) {
      return (
        <Box>
          {renderJobUrl()}

          {isExtracted && renderForm()}
        </Box>
      );
    }

    if (tabValue === 1) {
      return (
        <>
          <Box sx={boxStyle}>
            <JobsAutoComplete
              sx={{ marginBottom: 1 }}
              onSelect={job => {
                if (job) {
                  setExistingJobId(job._id);
                  setValue('title', job.title);
                  setValue('location', job.location || '');
                  setValue('locationType', job.locationType);
                  setValue('jobType', job.jobType);
                  setValue('jobTypeCustom', job.jobTypeCustom);
                  setValue('recruiterId', job.recruiterId || user?._id || '');
                  setValue('salary', job.salary);
                  setValue('attachments', job.attachments || []);
                  setValue('notes', job.notes);
                  setValue('description', job.description);

                  setValue('statusId', job.statusId || '');
                  setValue('clientId', job.clientId || '');

                  setCustomFieldsValues(job.customFieldsValues || {});
                  setClientName(job.clientName || '');
                  setValue('autosourceCurrentCompanyFilter', job.autosourceCurrentCompanyFilter);
                  setValue('autosourcePastCompanyFilter', job.autosourcePastCompanyFilter);
                  setValue('autosourceFunctionFilter', job.autosourceFunctionFilter);
                  setValue('autosourceCurrentJobTitleFilter', job.autosourceCurrentJobTitleFilter);
                  setValue('autosourceYearsInCurrrentCompanyFilter', job.autosourceYearsInCurrrentCompanyFilter);
                  setValue('autosourceLocationFilter', job.autosourceLocationFilter);
                  setValue('autosourceKeywordSearch', job.autosourceKeywordSearch);
                  setRecruiterLiteFields(buildRecruiterLiteFieldsFromJob(job));
                  setZipRecruiterFields(buildZipRecruiterFieldsFromJob(job));
                  setIndeedFields(buildIndeedFieldsFromJob(job));
                } else {
                  setExistingJobId('');
                }
              }}
            />
          </Box>
          {existingJobId && renderForm()}
        </>
      );
    }

    return renderForm();
  };

  const renderContent = () => {
    if (jobO._id) {
      return renderForm();
    }

    return (
      <StackTabs
        noSpacing={true}
        tabs={[
          { label: 'Create from URL', value: 0, testId: '@jobs-create-from-url' },
          { label: 'Create using existing job', value: 1, testId: '@jobs-create-using-existing-job' },
          { label: 'Create manually', value: 2, testId: '@jobs-create-manually' },
        ]}
        tabValue={tabValue}
        setTabValue={setTabValue}
        renderTabContent={renderTabContent}
      />
    );
  };

  const renderUpdateAutoSave = () => (
    <AutoSaveStatusIndicator status={updateAutosave.status} lastAutoSave={updateAutosave.lastAutoSave} type="job" />
  );

  const renderCreateAutoSave = () => {
    if (isExtracted || existingJobId || tabValue === 2) {
      return (
        <AutoSaveStatusIndicator
          status={autoSave.status}
          lastAutoSave={autoSave.lastAutoSave}
          type="job"
          onDiscard={() => {
            setValue('title', '');
            setValue('location', '');
            setValue('locationType', '');
            setValue('jobType', '');
            setValue('jobTypeCustom', '');
            setValue('recruiterId', user?._id || '');
            setValue('salary', '');
            setValue('attachments', []);
            setValue('notes', '');
            setValue('description', '');
            setValue('statusId', '');
            setValue('clientId', '');
            setValue('autosourceCurrentCompanyFilter', '');
            setValue('autosourcePastCompanyFilter', '');
            setValue('autosourceFunctionFilter', '');
            setValue('autosourceCurrentJobTitleFilter', '');
            setValue('autosourceYearsInCurrrentCompanyFilter', '');
            setValue('autosourceLocationFilter', '');
            setValue('autosourceKeywordSearch', '');
            setClientName('');
            setCustomFieldsValues({});
            setRecruiterLiteFields(emptyRecruiterLiteFields());
            setZipRecruiterFields(emptyZipRecruiterFields());
            setIndeedFields(emptyIndeedFields());

            setAttachmentsHash(Math.random().toString());
            setStatusHash(Math.random().toString());
            setClientHash(Math.random().toString());
            setEditorHash(Math.random().toString());

            autoSave.resetStatus();
          }}
        />
      );
    }
  };

  return (
    <Modal
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <span>{jobO.title ? jobO.title : 'Add'} Job</span>
          {isUpdate ? renderUpdateAutoSave() : renderCreateAutoSave()}
        </Box>
      }
      onClose={onClose}
      hideCloseButton={hideCloseButton}
      open={open}
      fullScreen
      sx={{
        width: {
          xs: '100%',
          md: '902px',
        },
        left: 'inherit',
      }}
      onSubmit={isUpdate ? undefined : handleSubmit(onSave, scrollToFirstFormErrorOnInvalid)}
      renderActions={() => {
        if (isUpdate) {
          return null;
        }

        if (isExtracted || existingJobId || tabValue === 2) {
          return (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving}
              data-testid="@job-form-submit"
            >
              Add
            </Button>
          );
        }

        if (extraFooterButtons) {
          return extraFooterButtons;
        }
      }}
      renderContent={() => renderContent()}
    />
  );
};

export default JobForm;
